import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Link,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DirectionsBus } from '@mui/icons-material';
import { sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../lib/firebase';
import { getClientIP } from '../utils/ipUtils';
import { logLoginAttempt } from '../services/loginAuditService';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [resetInfo, setResetInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const { login, user } = useAuth();
  const navigate = useNavigate();
  const isBusy = loading || resetLoading;

  useEffect(() => {
    // 로컬 스토리지에서 저장된 이메일 불러오기
    const savedEmail = localStorage.getItem('savedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    // 이미 로그인된 상태면 대시보드로 리다이렉트
    if (user) {
      // 자동 로그인 접근 기록
      const logAutoLogin = async () => {
        const ipAddress = await getClientIP();
        if (user.email) {
          await logLoginAttempt(user.email, ipAddress, true, 'auto');
        }
      };

      logAutoLogin();
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetInfo('');
    setLoading(true);

    // IP 주소 가져오기
    const ipAddress = await getClientIP();

    try {
      await login(email, password);

      // 로그인 성공 기록
      await logLoginAttempt(email, ipAddress, true, 'manual');

      // 이메일 저장 처리
      if (rememberMe) {
        localStorage.setItem('savedEmail', email);
      } else {
        localStorage.removeItem('savedEmail');
      }

      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);

      // Firebase 에러 메시지 처리
      let errorMessage = '로그인에 실패했습니다.';
      if (err.code === 'auth/invalid-credential') {
        errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = '존재하지 않는 사용자입니다.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = '비밀번호가 올바르지 않습니다.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = '올바른 이메일 형식이 아닙니다.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.';
      }

      // 로그인 실패 기록
      await logLoginAttempt(email, ipAddress, false, 'manual', err.code, errorMessage);

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const trimmedEmail = email.trim();
    setError('');
    setResetInfo('');

    if (!trimmedEmail) {
      setError('비밀번호 재설정 이메일을 받으려면 이메일을 입력해주세요.');
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, trimmedEmail);
      setResetInfo('비밀번호 재설정 이메일을 발송했습니다. 이메일을 확인해주세요.');
    } catch (err: any) {
      console.error('Password reset error:', err);

      if (err?.code === 'auth/user-not-found') {
        setResetInfo('비밀번호 재설정 이메일을 발송했습니다. 이메일을 확인해주세요.');
        return;
      }

      let errorMessage = '비밀번호 재설정 이메일 발송에 실패했습니다.';
      if (err?.code === 'auth/invalid-email') {
        errorMessage = '올바른 이메일 형식이 아닙니다.';
      } else if (err?.code === 'auth/too-many-requests') {
        errorMessage = '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.';
      }
      setError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                backgroundColor: '#667eea',
                borderRadius: '50%',
                p: 2,
                mb: 2,
              }}
            >
              <DirectionsBus sx={{ fontSize: 40, color: '#fff' }} />
            </Box>
            <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
              부산항 신항 셔틀버스
            </Typography>
            <Typography variant="body2" color="text.secondary">
              관리자 로그인
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {resetInfo && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {resetInfo}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="이메일"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
              disabled={isBusy}
            />
            <TextField
              fullWidth
              label="비밀번호"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              disabled={isBusy}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                  disabled={isBusy}
                />
              }
              label="아이디 저장"
              sx={{ mt: 1 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isBusy}
              sx={{
                mt: 3,
                mb: 1,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #63428b 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : '로그인'}
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Link
                component="button"
                type="button"
                onClick={handlePasswordReset}
                underline="hover"
                aria-disabled={isBusy}
                tabIndex={isBusy ? -1 : 0}
                sx={{
                  fontSize: 14,
                  color: isBusy ? 'text.disabled' : 'primary.main',
                  pointerEvents: isBusy ? 'none' : 'auto',
                }}
              >
                {resetLoading ? '비밀번호 재설정 이메일 발송 중…' : '비밀번호 찾기'}
              </Link>
            </Box>
          </form>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              관리자 계정으로만 로그인이 가능합니다.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
